import React, { useState, useRef, useEffect } from "react";
import Cookies from "js-cookie";
import { useNavigate, useLocation } from "react-router-dom";
import BarChartIcon from "@mui/icons-material/BarChart";
import GridViewIcon from "@mui/icons-material/GridView";
import TableChartIcon from "@mui/icons-material/TableChart";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import InfoIcon from "@mui/icons-material/Info";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import CheckIcon from "@mui/icons-material/Check";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import SaveIcon from "@mui/icons-material/Save";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Slide, toast } from "react-toastify";
import { fetchProfile } from "../../../utils/fetchProfile";
import Navbar from "../../../Components/Navbar";
import { LEVEL_OPTION } from "../../../utils/constant";

const MODULE_NAME = "tabular";

function extractUnique(data, key) {
  return [...new Set(data.map((d) => d[key]))]
    .filter((v) => v != null && v !== "")
    .sort((a, b) => String(a).localeCompare(String(b)));
}

const fetchTypeProject = async (accessToken) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/v1/report_db/db_set`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      }
    );
    if (response.ok) {
      const data = await response.json();
      return Array.isArray(data.data) ? data.data : [];
    }
    return [];
  } catch (error) {
    console.error("Error fetching DB Sets:", error);
    return [];
  }
};

const fetchFacts = async (accessToken, dbSet) => {
  try {
    const url = dbSet
      ? `${
          process.env.REACT_APP_API_URL
        }/api/v1/report_db/fact?db_set=${encodeURIComponent(dbSet)}`
      : `${process.env.REACT_APP_API_URL}/api/v1/report_db/fact`;
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    if (response.ok) {
      const data = await response.json();
      return Array.isArray(data.data) ? data.data : [];
    }
    return [];
  } catch (error) {
    console.error("Error fetching Facts:", error);
    return [];
  }
};

const fetchPeriods = async (accessToken, dbSet) => {
  try {
    const url = dbSet
      ? `${
          process.env.REACT_APP_API_URL
        }/api/v1/report_db/period?db_set=${encodeURIComponent(dbSet)}`
      : `${process.env.REACT_APP_API_URL}/api/v1/report_db/period`;
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    if (response.ok) {
      const data = await response.json();
      return Array.isArray(data.data) ? data.data : [];
    }
    return [];
  } catch (error) {
    console.error("Error fetching Periods:", error);
    return [];
  }
};

const fetchLevels = async (accessToken, dbSet) => {
  try {
    const url = dbSet
      ? `${
          process.env.REACT_APP_API_URL
        }/api/v1/report_db/level?db_set=${encodeURIComponent(dbSet)}`
      : `${process.env.REACT_APP_API_URL}/api/v1/report_db/level`;
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    if (response.ok) {
      const data = await response.json();
      return Array.isArray(data.data) ? data.data : [];
    }
    return [];
  } catch (error) {
    console.error("Error fetching Levels:", error);
    return [];
  }
};

const fetchMarketBreakdowns = async (accessToken, dbSet) => {
  try {
    const url = dbSet
      ? `${
          process.env.REACT_APP_API_URL
        }/api/v1/report_db/market_breakdown?db_set=${encodeURIComponent(dbSet)}`
      : `${process.env.REACT_APP_API_URL}/api/v1/report_db/market_breakdown`;
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    if (response.ok) {
      const data = await response.json();
      return Array.isArray(data.data) ? data.data : [];
    }
    return [];
  } catch (error) {
    console.error("Error fetching Market Breakdowns:", error);
    return [];
  }
};

const fetchCategoryMaster = async (accessToken) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/v1/category-master`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      }
    );
    if (response.ok) {
      const data = await response.json();
      return Array.isArray(data.data) ? data.data : [];
    }
    return [];
  } catch (error) {
    console.error("Error fetching category master:", error);
    return [];
  }
};

const fetchBrandMaster = async (accessToken) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/v1/brand-master`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      }
    );
    if (response.ok) {
      const data = await response.json();
      return Array.isArray(data.data) ? data.data : [];
    }
    return [];
  } catch (error) {
    console.error("Error fetching brand master:", error);
    return [];
  }
};

const fetchProductMaster = async (accessToken, params = {}) => {
  try {
    const url = new URL(`${process.env.REACT_APP_API_URL}/api/v1/product-master`);
    Object.keys(params).forEach((key) => {
      if (params[key] !== undefined && params[key] !== null) {
        url.searchParams.append(key, params[key]);
      }
    });
    const response = await fetch(
      url.toString(),
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      }
    );
    if (response.ok) {
      const data = await response.json();
      return Array.isArray(data.data) ? data.data : [];
    }
    return [];
  } catch (error) {
    console.error("Error fetching product master:", error);
    return [];
  }
};

const fetchRawData = async (accessToken, params = {}) => {
  try {
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(
        ([key, value]) =>
          value !== "" &&
          value !== null &&
          (!Array.isArray(value) || value.length > 0)
      )
    );
    Object.keys(filteredParams).forEach((key) => {
      if (Array.isArray(filteredParams[key])) {
        filteredParams[key] = filteredParams[key].join(",");
      } else if (key === "period") {
        filteredParams[key] = String(filteredParams[key]);
      }
    });
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/v1/report_db`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(filteredParams),
      }
    );
    if (response.ok) {
      const data = await response.json();
      const rows = data.data || [];
      return rows.map((row) => ({
        ...row,
        Category: row.Group || row.category || row.Category,
        category: row.Group || row.category || row.Category,
      }));
    }
    return [];
  } catch (error) {
    console.error("Error fetching raw data:", error);
    return [];
  }
};

/* ── Config Template API helpers ── */ 
const fetchConfigTemplates = async (accessToken) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/v1/config_template?module_name=${MODULE_NAME}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      }
    );
    if (response.ok) {
      const res = await response.json();
      return Array.isArray(res.data) ? res.data : [];
    }
    return [];
  } catch (error) {
    console.error("Error fetching config templates:", error);
    return [];
  }
};

const createConfigTemplate = async (
  accessToken,
  templateName,
  filterPayload
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/v1/config_template`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          module_name: MODULE_NAME,
          template_name: templateName,
          filter_payload: JSON.stringify(filterPayload),
        }),
      }
    );
    if (response.ok) {
      const res = await response.json();
      return res.data || null;
    }
    return null;
  } catch (error) {
    console.error("Error creating config template:", error);
    return null;
  }
};

const updateConfigTemplate = async (
  accessToken,
  templateId,
  templateName,
  filterPayload
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/v1/config_template/${templateId}`,
      {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          template_name: templateName,
          filter_payload: JSON.stringify(filterPayload),
        }),
      }
    );
    if (response.ok) {
      const res = await response.json();
      return res.data || null;
    }
    return null;
  } catch (error) {
    console.error("Error updating config template:", error);
    return null;
  }
};

const deleteConfigTemplate = async (accessToken, templateId) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/v1/config_template/${templateId}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      }
    );
    return response.ok;
  } catch (error) {
    console.error("Error deleting config template:", error);
    return false;
  }
};

function LoadingDots() {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 3, marginLeft: 6 }}>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          style={{
            width: 5,
            height: 5,
            borderRadius: "50%",
            background: "#07a3b5",
            animation: "dotPulse 1s infinite alternate",
            animationDelay: `${i * 0.2}s`,
          }}
        />
      ))}
    </span>
  );
}

function SearchSelect({
  label,
  options,
  value,
  onChange,
  required,
  placeholder = "Semua",
  loading = false,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  const filtered = options.filter((o) =>
    String(o).toLowerCase().includes(q.toLowerCase())
  );
  const isErr = required && !value;
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div
        style={{
          fontSize: "0.72rem",
          fontWeight: 700,
          color: isErr ? "#e74c3c" : "#64748b",
          textTransform: "uppercase",
          letterSpacing: "0.6px",
          marginBottom: 6,
          display: "flex",
          alignItems: "center",
        }}
      >
        {label}
        {required && <span style={{ color: "#e74c3c" }}> *</span>}
        {loading && <LoadingDots />}
      </div>
      <div
        onClick={() => !loading && setOpen((p) => !p)}
        style={{
          minHeight: 40,
          padding: "5px 10px",
          border: `1.5px solid ${
            isErr ? "#fca5a5" : open ? "#07a3b5" : "#e2e8f0"
          }`,
          borderRadius: 8,
          background: loading ? "#f8fafc" : "white",
          cursor: loading ? "not-allowed" : "pointer",
          display: "flex",
          flexWrap: "wrap",
          gap: 4,
          alignItems: "center",
          boxShadow: open ? "0 0 0 3px rgba(7,163,181,0.12)" : "none",
          transition: "all .15s",
        }}
      >
        {value ? (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              background: "#07a3b5",
              color: "white",
              borderRadius: 20,
              padding: "2px 10px",
              fontSize: "0.78rem",
              fontWeight: 600,
              lineHeight: "22px",
            }}
          >
            {value}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onChange("");
              }}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                padding: 0,
                color: "white",
              }}
            >
              <CloseIcon sx={{ fontSize: 13 }} />
            </button>
          </span>
        ) : (
          <span
            style={{
              fontSize: "0.85rem",
              color: loading ? "#b0bec5" : "#94a3b8",
              lineHeight: "28px",
            }}
          >
            {loading ? "Memuat opsi…" : placeholder}
          </span>
        )}
        <KeyboardArrowDownIcon
          sx={{
            fontSize: 18,
            color: "#94a3b8",
            marginLeft: "auto",
            flexShrink: 0,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform .2s",
          }}
        />
      </div>
      {isErr && (
        <p style={{ margin: "3px 0 0", fontSize: "0.7rem", color: "#e74c3c" }}>
          Wajib dipilih
        </p>
      )}
      {open && !loading && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            background: "white",
            border: "1.5px solid #e2e8f0",
            borderRadius: 10,
            boxShadow: "0 8px 30px rgba(0,0,0,.12)",
            zIndex: 9999,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "8px 10px",
              borderBottom: "1px solid #f1f5f9",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <SearchIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cari…"
              style={{
                border: "none",
                outline: "none",
                fontSize: "0.82rem",
                width: "100%",
                fontFamily: "inherit",
                color: "#334155",
              }}
            />
          </div>
          <div style={{ maxHeight: 220, overflowY: "auto" }}>
            <button
              onClick={() => {
                onChange("");
                setOpen(false);
                setQ("");
              }}
              style={{
                width: "100%",
                padding: "9px 12px",
                border: "none",
                background: !value ? "#f0fdfe" : "white",
                cursor: "pointer",
                fontSize: "0.82rem",
                textAlign: "left",
                fontFamily: "inherit",
                color: "#64748b",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              {!value && <CheckIcon sx={{ fontSize: 15, color: "#07a3b5" }} />}
              <em>— {placeholder} —</em>
            </button>
            {filtered.map((opt) => (
              <button
                key={opt}
                onClick={() => {
                  onChange(opt);
                  setOpen(false);
                  setQ("");
                }}
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  border: "none",
                  background: value === opt ? "#f0fdfe" : "white",
                  cursor: "pointer",
                  fontSize: "0.82rem",
                  textAlign: "left",
                  fontFamily: "inherit",
                  color: "#0f172a",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                {value === opt && (
                  <CheckIcon sx={{ fontSize: 15, color: "#07a3b5" }} />
                )}
                <span style={{ marginLeft: value === opt ? 0 : 23 }}>
                  {opt}
                </span>
              </button>
            ))}
            {filtered.length === 0 && (
              <div style={{ padding: 12, textAlign: "center", color: "#94a3b8", fontSize: "0.8rem" }}>
                Tidak ditemukan
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MultiSearchSelect({
  label,
  options,
  value = [],
  onChange,
  required,
  placeholder = "Semua",
  hideSelectAll = false,
  singleSelect = false,
  loading = false,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  const filtered = options.filter((o) =>
    String(o).toLowerCase().includes(q.toLowerCase())
  );
  const safeValue = Array.isArray(value) ? value : [];
  const isErr = required && safeValue.length === 0;

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (opt) => {
    if (singleSelect) {
      onChange([opt]);
      setOpen(false);
    } else {
      if (safeValue.includes(opt)) onChange(safeValue.filter((v) => v !== opt));
      else onChange([...safeValue, opt]);
    }
  };
  const selectAll = () => {
    if (singleSelect) return;
    onChange(safeValue.length === options.length ? [] : [...options]);
  };
  const remove = (opt, e) => {
    e.stopPropagation();
    if (singleSelect) return;
    onChange(safeValue.filter((v) => v !== opt));
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div
        style={{
          fontSize: "0.72rem",
          fontWeight: 700,
          color: isErr ? "#e74c3c" : "#64748b",
          textTransform: "uppercase",
          letterSpacing: "0.6px",
          marginBottom: 6,
          display: "flex",
          alignItems: "center",
        }}
      >
        {label}
        {required && <span style={{ color: "#e74c3c" }}> *</span>}
        {loading && <LoadingDots />}
      </div>
      <div
        onClick={() => !loading && setOpen((p) => !p)}
        style={{
          minHeight: 40,
          padding: "5px 10px",
          border: `1.5px solid ${
            isErr ? "#fca5a5" : open ? "#07a3b5" : "#e2e8f0"
          }`,
          borderRadius: 8,
          background: loading ? "#f8fafc" : "white",
          cursor: loading ? "not-allowed" : "pointer",
          display: "flex",
          flexWrap: "wrap",
          gap: 4,
          alignItems: "center",
          boxShadow: open ? "0 0 0 3px rgba(7,163,181,0.12)" : "none",
          transition: "all .15s",
        }}
      >
        {safeValue.length === 0 && (
          <span
            style={{
              fontSize: "0.85rem",
              color: loading ? "#b0bec5" : "#94a3b8",
              lineHeight: "28px",
            }}
          >
            {loading ? "Memuat opsi…" : placeholder}
          </span>
        )}
        {safeValue.map((v) => (
          <span
            key={v}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              background: "#e0f7fa",
              color: "#07a3b5",
              borderRadius: 20,
              padding: singleSelect ? "2px 10px" : "2px 6px 2px 10px",
              fontSize: "0.78rem",
              fontWeight: 600,
              lineHeight: "22px",
            }}
          >
            {v}
            {!singleSelect && (
              <button
                onClick={(e) => remove(v, e)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  padding: 0,
                  color: "#07a3b5",
                }}
              >
                <CloseIcon sx={{ fontSize: 13 }} />
              </button>
            )}
          </span>
        ))}
        <KeyboardArrowDownIcon
          sx={{
            fontSize: 18,
            color: "#94a3b8",
            marginLeft: "auto",
            flexShrink: 0,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform .2s",
          }}
        />
      </div>
      {isErr && (
        <p style={{ margin: "3px 0 0", fontSize: "0.7rem", color: "#e74c3c" }}>
          Wajib dipilih
        </p>
      )}
      {open && !loading && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            background: "white",
            border: "1.5px solid #e2e8f0",
            borderRadius: 10,
            boxShadow: "0 8px 30px rgba(0,0,0,.12)",
            zIndex: 9999,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "8px 10px",
              borderBottom: "1px solid #f1f5f9",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <SearchIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cari…"
              style={{
                border: "none",
                outline: "none",
                fontSize: "0.82rem",
                width: "100%",
                fontFamily: "inherit",
                color: "#334155",
              }}
              onClick={(e) => e.stopPropagation()}
            />
            {q && (
              <button
                onClick={() => setQ("")}
                style={{
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  padding: 0,
                  display: "flex",
                }}
              >
                <CloseIcon sx={{ fontSize: 15, color: "#94a3b8" }} />
              </button>
            )}
          </div>
          {!hideSelectAll && (
            <div
              style={{
                padding: "4px 6px 2px",
                borderBottom: "1px solid #f1f5f9",
              }}
            >
              <button
                onClick={selectAll}
                style={{
                  width: "100%",
                  padding: "7px 8px",
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  fontSize: "0.8rem",
                  textAlign: "left",
                  fontFamily: "inherit",
                  color: "#475569",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  borderRadius: 6,
                }}
              >
                <div
                  style={{
                    width: 16,
                    height: 16,
                    border: "1.5px solid",
                    borderColor:
                      safeValue.length === options.length ? "#07a3b5" : "#cbd5e1",
                    borderRadius: 4,
                    background:
                      safeValue.length === options.length ? "#07a3b5" : "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {safeValue.length === options.length && (
                    <CheckIcon sx={{ fontSize: 11, color: "white" }} />
                  )}
                </div>
                <em>Pilih Semua ({options.length})</em>
              </button>
            </div>
          )}
          <div style={{ maxHeight: 200, overflowY: "auto" }}>
            {filtered.map((opt) => (
              <button
                key={opt}
                onClick={() => toggle(opt)}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  fontSize: "0.82rem",
                  textAlign: "left",
                  fontFamily: "inherit",
                  color: "#0f172a",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <div
                  style={{
                    width: 16,
                    height: 16,
                    border: "1.5px solid",
                    borderColor: safeValue.includes(opt) ? "#07a3b5" : "#cbd5e1",
                    borderRadius: 4,
                    background: safeValue.includes(opt) ? "#07a3b5" : "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {safeValue.includes(opt) && (
                    <CheckIcon sx={{ fontSize: 11, color: "white" }} />
                  )}
                </div>
                {opt}
              </button>
            ))}
            {filtered.length === 0 && (
              <div
                style={{
                  padding: "12px",
                  textAlign: "center",
                  color: "#94a3b8",
                  fontSize: "0.8rem",
                }}
              >
                Tidak ditemukan
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const iconBtn = (color) => ({
  border: `1.5px solid ${color}`,
  color,
  background: "transparent",
  borderRadius: 7,
  padding: 6,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: "inherit",
});

function FilterZone({ title, icon: Icon, children, rightSlot }) {
  return (
    <div
      style={{
        background: "#f8fafc",
        border: "1px solid #e8edf2",
        borderRadius: 10,
        padding: "16px 18px",
        marginBottom: 20,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {Icon && <Icon sx={{ fontSize: 18, color: "#64748b" }} />}
          <span
            style={{ fontWeight: 700, color: "#374151", fontSize: "0.92rem" }}
          >
            {title}
          </span>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {rightSlot}
        </div>
      </div>
      {children}
    </div>
  );
}

function TplModal({ open, mode, defaultName, onClose, onConfirm }) {
  const [name, setName] = useState("");
  useEffect(() => {
    if (open) setName(mode === "save" ? defaultName || "" : "");
  }, [open, mode, defaultName]);
  if (!open) return null;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.5)",
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backdropFilter: "blur(3px)",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: 14,
          boxShadow: "0 24px 60px rgba(0,0,0,.2)",
          padding: "28px 32px",
          width: 420,
          maxWidth: "92vw",
        }}
      >
        <h5
          style={{
            margin: "0 0 4px",
            fontWeight: 800,
            color: "#0f172a",
            fontSize: "1.05rem",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <SaveIcon sx={{ fontSize: 20, color: "#07a3b5" }} />{" "}
          {mode === "save" ? "Simpan Template" : "Simpan Sebagai Baru"}
        </h5>
        <p
          style={{ margin: "0 0 20px", color: "#64748b", fontSize: "0.82rem" }}
        >
          {mode === "save"
            ? "Perbarui template yang sedang aktif."
            : "Buat template baru dari konfigurasi saat ini."}
        </p>
        <label
          style={{
            fontSize: "0.72rem",
            fontWeight: 700,
            color: "#475569",
            textTransform: "uppercase",
            letterSpacing: "0.6px",
          }}
        >
          Nama Template
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Cth: Analisis Jakarta Q1…"
          autoFocus
          onKeyDown={(e) =>
            e.key === "Enter" && name.trim() && onConfirm(name.trim())
          }
          style={{
            width: "100%",
            marginTop: 8,
            padding: "10px 12px",
            boxSizing: "border-box",
            border: "1.5px solid #e2e8f0",
            borderRadius: 8,
            fontSize: "0.88rem",
            outline: "none",
            fontFamily: "inherit",
          }}
          onFocus={(e) => (e.target.style.borderColor = "#07a3b5")}
          onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
        />
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 20,
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "8px 18px",
              border: "1.5px solid #e2e8f0",
              borderRadius: 8,
              background: "white",
              color: "#475569",
              cursor: "pointer",
              fontWeight: 600,
              fontFamily: "inherit",
            }}
          >
            Batal
          </button>
          <button
            onClick={() => name.trim() && onConfirm(name.trim())}
            style={{
              padding: "8px 20px",
              border: "none",
              borderRadius: 8,
              background: "#07a3b5",
              color: "white",
              cursor: "pointer",
              fontWeight: 700,
              fontFamily: "inherit",
            }}
          >
            {mode === "save" ? "Simpan" : "Buat Baru"}
          </button>
        </div>
      </div>
    </div>
  );
}

  const makeTableF = () => ({
  DB: "",
  Fact: [],
  Market: [],
  Period: [],
  Level: ["1_Category"],
  Category: [],
  Brand: [],
  Product: [],
});

const thStyle = {
  padding: "12px 16px",
  textAlign: "left",
  fontWeight: 700,
  color: "#334155",
  borderBottom: "2px solid #e2e8f0",
  whiteSpace: "nowrap",
};
const tdStyle = { padding: "10px 16px", color: "#475569" };
const pageBtnStyle = (disabled) => ({
  padding: "6px 14px",
  border: "1px solid #e2e8f0",
  borderRadius: 6,
  background: disabled ? "#f8fafc" : "white",
  color: disabled ? "#94a3b8" : "#334155",
  cursor: disabled ? "not-allowed" : "pointer",
  fontWeight: 600,
  fontSize: "0.85rem",
  fontFamily: "inherit",
});

export default function ReportProjectedNewRawData() {
  const accessToken = Cookies.get("access_token") || "";
  const navigate = useNavigate();
  const location = useLocation();
  const [dataProfile, setDataProfile] = useState({});

  const [tpls, setTpls] = useState([]);
  const [selTpl, setSelTpl] = useState("");
  const [modal, setModal] = useState({
    open: false,
    mode: "saveas",
    payload: {},
  });

  // Drilldown states from React Router location
  const [drilldown, setDrilldown] = useState(location.state?.drilldown || null);
  const [initialSearch] = useState(location.state?.drillSearch || "");
  const [initialData] = useState(location.state?.drillData || []);

  const [tf, setTf] = useState(makeTableF());
  const [appliedFilters, setAppliedFilters] = useState({
    Category: [],
    Brand: [],
    Product: [],
  });
  const [loadingDims, setLoadingDims] = useState({
    Fact: false,
    Period: false,
    Market: false,
    Category: false,
    Products: false,
  });
  const loadedDB = useRef("");
  const loadedLevel = useRef("");
  const loadedCat = useRef("");
  const [apiData, setApiData] = useState([]);
  const [busy, setBusy] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(initialSearch || "");
  const itemsPerPage = 50;

  const [dims, setDims] = useState({
    DB: [],
    Fact: [],
    Period: [],
    Market: [],
    Level: [],
    Category: [],
    Brand: [],
    Product: [],
  });

  const [masterCategories, setMasterCategories] = useState([]);
  const [masterBrands, setMasterBrands] = useState([]);
  const [masterProducts, setMasterProducts] = useState([]);

  useEffect(() => {
    fetchProfile(accessToken, setDataProfile, []);
  }, [accessToken]);

  // Load config templates from API on mount
  useEffect(() => {
    const loadTemplates = async () => {
      if (!accessToken) return;
      const templates = await fetchConfigTemplates(accessToken);
      setTpls(templates);
    };
    loadTemplates();
  }, [accessToken]);

  useEffect(() => {
    const loadDimensions = async () => {
      const dbs = await fetchTypeProject(accessToken);
      setDims((p) => ({ ...p, DB: dbs }));
    };
    if (accessToken) loadDimensions();
  }, [accessToken]);

  useEffect(() => {
    if (!tf.DB) return;

    const selectedLevels = tf.Level || [];
    const showCategory = selectedLevels.includes("1_Category") || selectedLevels.includes("3_Brand") || selectedLevels.includes("4_Product");
    const showBrand = selectedLevels.includes("3_Brand") || selectedLevels.includes("4_Product");
    const showProduct = selectedLevels.includes("4_Product");

    const levelKey = (tf.Level || []).slice().sort().join(",");
    const categoryKey = (tf.Category || []).slice().sort().join(",");
    const cacheKey = `${levelKey}|${categoryKey}`;
    if (cacheKey === loadedLevel.current) return;

    const needsCategory = showCategory;
    const needsProducts = showBrand || showProduct;

    if (!needsCategory && !needsProducts) {
      loadedLevel.current = cacheKey;
      return;
    }

    const loadMasterData = async () => {
      let cats = masterCategories;

      if (needsCategory && masterCategories.length === 0) {
        setLoadingDims((p) => ({ ...p, Category: true }));
        cats = await fetchCategoryMaster(accessToken);
        setMasterCategories(cats);
        setDims((p) => ({ ...p, Category: cats.map((c) => c.ord_category).filter(Boolean) }));
        setLoadingDims((p) => ({ ...p, Category: false }));
      }

      if (needsProducts) {
        if (categoryKey === loadedCat.current) {
          loadedLevel.current = cacheKey;
          return;
        }
        loadedCat.current = categoryKey;
        setLoadingDims((p) => ({ ...p, Products: true }));

        let prods = [];
        if (tf.Category?.length > 0) {
          const ids = tf.Category.map((val) => {
            const obj = cats.find((c) => c.ord_category === val || c.name === val);
            return obj ? obj.id : null;
          }).filter(Boolean);
          prods = ids.length > 0
            ? await fetchProductMaster(accessToken, { column_name: "category_master_id", column_operator: "IN", column_value: ids.join(",") })
            : await fetchProductMaster(accessToken);
        } else {
          prods = await fetchProductMaster(accessToken);
        }

        setMasterProducts(prods);
        setDims((p) => ({ ...p, Product: prods.map((pr) => pr.product_name).filter(Boolean) }));
        setLoadingDims((p) => ({ ...p, Products: false }));
      }

      loadedLevel.current = cacheKey;
    };

    loadMasterData();
  }, [tf.DB, tf.Level, tf.Category, accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!tf.DB || tf.DB === loadedDB.current) return;
    loadedDB.current = tf.DB;
    loadedLevel.current = "";
    loadedCat.current = "";

    setDims((p) => ({ ...p, Fact: [], Period: [], Market: [], Category: [], Brand: [], Product: [] }));
    setMasterCategories([]);
    setMasterProducts([]);
    setLoadingDims((p) => ({ ...p, Fact: true, Period: true, Market: true }));

    const loadFilterData = async () => {
      try {
        const [facts, periods, levels, markets] = await Promise.all([
          fetchFacts(accessToken, tf.DB),
          fetchPeriods(accessToken, tf.DB),
          fetchLevels(accessToken, tf.DB),
          fetchMarketBreakdowns(accessToken, tf.DB),
        ]);
        setDims((p) => ({
          ...p,
          Fact: facts,
          Period: periods,
          Level: levels,
          Market: markets,
        }));
      } catch (e) {
        console.error("Error loading filters", e);
      } finally {
        setLoadingDims((p) => ({ ...p, Fact: false, Period: false, Market: false }));
      }
    };
    loadFilterData();
  }, [tf.DB, accessToken]);

  useEffect(() => {
    if (drilldown && initialData && initialData.length > 0) {
      setApiData(initialData);
      setSearch(initialSearch || "");
    }
  }, [drilldown, initialData, initialSearch]);

  const tableValid = () =>
    tf.DB &&
    (Array.isArray(tf.Fact) ? tf.Fact.length > 0 : tf.Fact) &&
    (Array.isArray(tf.Market) ? tf.Market.length > 0 : tf.Market) &&
    (Array.isArray(tf.Period) ? tf.Period.length > 0 : tf.Period);

  const brandOpts = masterProducts.length
    ? [
        ...new Set(masterProducts.map((p) => p.brand_master)),
      ].filter(Boolean).sort()
    : dims.Brand;

  const prodOpts = (() => {
    let filtered = masterProducts;
    if (tf.Brand?.length) {
      filtered = filtered.filter((p) => tf.Brand.includes(p.brand_master));
    }
    return [...new Set(filtered.map((p) => p.product_name))].filter(Boolean).sort();
  })();
  function loadTpl(idx) {
    if (idx === "") return;
    const t = tpls.find((tpl) => tpl.id === idx);
    if (!t) return;
    try {
      const payload =
        typeof t.filter_payload === "string"
          ? JSON.parse(t.filter_payload)
          : t.filter_payload;
      const loadedTf = { ...makeTableF(), ...(payload.tf || {}) };
      if (loadedTf.Fact && !Array.isArray(loadedTf.Fact)) {
        loadedTf.Fact = [loadedTf.Fact];
      }
      if (loadedTf.Period && !Array.isArray(loadedTf.Period)) {
        loadedTf.Period = [loadedTf.Period];
      }
      if (loadedTf.Market && !Array.isArray(loadedTf.Market)) {
        loadedTf.Market = [loadedTf.Market];
      }
      setTf(loadedTf);
    } catch (e) {
      console.error("Error parsing template payload:", e);
    }
  }

  function openModal(page, mode, payload) {
    setModal({ open: true, mode, payload });
  }

  async function confirmModal(name) {
    const { mode, payload } = modal;
    if (mode === "save" && selTpl !== "") {
      const result = await updateConfigTemplate(
        accessToken,
        selTpl,
        name,
        payload
      );
      if (result) {
        const updated = await fetchConfigTemplates(accessToken);
        setTpls(updated);
      }
    } else {
      const result = await createConfigTemplate(accessToken, name, payload);
      if (result) {
        const updated = await fetchConfigTemplates(accessToken);
        setTpls(updated);
        setSelTpl(result.id);
      }
    }
    setModal((m) => ({ ...m, open: false }));
  }

  async function handleDeleteTpl() {
    if (!window.confirm("Hapus template ini?")) return;
    const ok = await deleteConfigTemplate(accessToken, selTpl);
    if (ok) {
      const updated = await fetchConfigTemplates(accessToken);
      setTpls(updated);
      setSelTpl("");
    }
  }
  async function generateTable() {
    if (!tableValid()) return;
    setBusy(true);
    setDrilldown(null);
    setSearch("");
    setAppliedFilters({
      Category: tf.Category,
      Brand: tf.Brand,
      Product: tf.Product,
    });
    try {
      const filterParams = {
        namagroup: tf.Category && tf.Category.length > 0 ? tf.Category : undefined,
        brand: tf.Brand && tf.Brand.length > 0 ? tf.Brand : undefined,
        product: tf.Product && tf.Product.length > 0 ? tf.Product : undefined,
      };

      const data = await fetchRawData(accessToken, {
        db_set: tf.DB,
        period: tf.Period,
        market_breakdown: tf.Market,
        level: tf.Level,
        fact: tf.Fact,
        size: 5000,
        page: 1,
        ...filterParams,
      });
      setApiData(data);
      setPage(1);
    } catch (error) {
      console.error("Error generating table:", error);
    } finally {
      setBusy(false);
    }
  }
  async function handleProcessExport() {
    if (!tableValid()) return;
    setIsLoading(true);
    try {
      const formData = {
        db_set: tf.DB,
        fact: Array.isArray(tf.Fact) ? tf.Fact.join(",") : tf.Fact,
        period: Array.isArray(tf.Period) && tf.Period.length > 0
          ? tf.Period.map(String).join(",")
          : (dims.Period && dims.Period.length > 0 ? dims.Period.map(String).join(",") : ""),
        level: Array.isArray(tf.Level) && tf.Level.length > 0
          ? tf.Level.join(",")
          : (dims.Level && dims.Level.length > 0 ? dims.Level.join(",") : ""),
        market_breakdown: Array.isArray(tf.Market) ? tf.Market.join(",") : tf.Market,
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/report_db/process_queue`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(formData),
        }
      );

      if (
        response.status === 201 ||
        response.status === 200 ||
        response.status === 202
      ) {
        toast.success("data akan diproses kedalam antrian", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Slide,
        });
      } else if (response.status === 400) {
        const message = await response.json();
        toast.error(message?.detail || "Gagal", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Slide,
        });
      } else if (response.status === 404) {
        toast.error("data tidak ditemukan", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Slide,
        });
      } else {
        toast.error("ada masalah saat menyimpan data", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Slide,
        });
      }
    } catch (error) {
      console.error("Error creating process queue:", error);
    }
    setIsLoading(false);
  }

  const handleExport = async () => {
    if (!tableValid()) return;
    setIsLoading(true);
    try {
      const formData = {
        db_set: tf.DB,
        fact: Array.isArray(tf.Fact) ? tf.Fact.join(",") : tf.Fact,
        level: Array.isArray(tf.Level) && tf.Level.length > 0
          ? tf.Level.join(",")
          : (dims.Level && dims.Level.length > 0 ? dims.Level.join(",") : ""),
        period: Array.isArray(tf.Period) && tf.Period.length > 0
          ? tf.Period.map(String).join(",")
          : (dims.Period && dims.Period.length > 0 ? dims.Period.map(String).join(",") : ""),
        market_breakdown: Array.isArray(tf.Market) ? tf.Market.join(",") : tf.Market,
      };

      const apiUrl = `${process.env.REACT_APP_API_URL}/api/v1/report_db/export`;

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.status === 200) {
        const blob = await response.blob();
        const filename = `report_rawdata_${tf.Level}_${tf.DB}_${tf.Fact}.xlsx`;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
      } else if (response.status === 400 || response.status === 404) {
        const data = await response.json();
        toast.error(data.detail || "Gagal mengunduh", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Slide,
        });
      } else {
        toast.error("Gagal mengunduh", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Slide,
        });
        console.error("Failed to export process queue:", response.statusText);
      }
    } catch (error) {
      console.error("Error export process queue:", error);
    }
    setIsLoading(false);
  };

  function reset() {
    setTf(makeTableF());
    setApiData([]);
    setDrilldown(null);
    setSearch("");
    setAppliedFilters({
      Category: [],
      Brand: [],
      Product: [],
    });
  }

  const filteredData = apiData.filter((d) => {
    if (appliedFilters.Category?.length && !appliedFilters.Category.includes(d.Category)) return false;
    if (appliedFilters.Brand?.length && !appliedFilters.Brand.includes(d.Brand)) return false;
    if (appliedFilters.Product?.length && !appliedFilters.Product.includes(d.Product)) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      const match = Object.values(d).some((val) =>
        String(val).toLowerCase().includes(q)
      );
      if (!match) return false;
    }
    return true;
  });

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const currentData = filteredData.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  return (
    <>
      <style>{`
        * { box-sizing: border-box; }
        .hdr-btn:hover { opacity: 0.85; }
        @keyframes dotPulse {
          0%, 80%, 100% { opacity: 0.2; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1); }
        }
      `}</style>

      <Navbar
        active="Projected Reporting"
        initial={dataProfile.initial}
        role_code={dataProfile.role_code}
      />

      <TplModal
        open={modal.open}
        mode={modal.mode}
        defaultName={
          selTpl !== "" ? tpls.find((t) => t.id === selTpl)?.template_name : ""
        }
        onClose={() => setModal((m) => ({ ...m, open: false }))}
        onConfirm={confirmModal}
      />

      <div style={{ background: "#f0f4f8", minHeight: "calc(100vh - 64px)" }}>
        <div
          style={{ maxWidth: "100%", margin: "0 auto", padding: "16px 16px" }}
        >
          {/* Navigation Buttons Header */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: 16,
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 2,
                background: "white",
                border: "1px solid #e2e8f0",
                borderRadius: 12,
                padding: 4,
                boxShadow: "0 1px 6px rgba(0,0,0,.06)",
              }}
            >
              <button
                onClick={() => navigate("/projected-reporting-pivot")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  padding: "9px 28px",
                  borderRadius: 9,
                  border: "none",
                  cursor: "pointer",
                  fontWeight: 500,
                  fontSize: "0.88rem",
                  fontFamily: "inherit",
                  background: "transparent",
                  color: "#64748b",
                  transition: "all .18s",
                }}
              >
                <GridViewIcon sx={{ fontSize: 18 }} /> Pivot Report View
              </button>
              <button
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  padding: "9px 28px",
                  borderRadius: 9,
                  border: "none",
                  cursor: "pointer",
                  fontWeight: 700,
                  fontSize: "0.88rem",
                  fontFamily: "inherit",
                  background: "#07a3b5",
                  color: "white",
                  boxShadow: "0 3px 10px rgba(7,163,181,0.3)",
                  transition: "all .18s",
                }}
              >
                <TableChartIcon sx={{ fontSize: 18 }} /> Tabular Report View
              </button>
              <button
                onClick={() => navigate("/projected-reporting")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  padding: "9px 28px",
                  borderRadius: 9,
                  border: "none",
                  cursor: "pointer",
                  fontWeight: 500,
                  fontSize: "0.88rem",
                  fontFamily: "inherit",
                  background: "transparent",
                  color: "#64748b",
                  transition: "all .18s",
                }}
              >
                <BarChartIcon sx={{ fontSize: 18 }} /> Custom Chart Report View
              </button>
            </div>
          </div>

          {/* Table View Content */}
          <div
            style={{
              background: "white",
              borderRadius: 12,
              boxShadow: "0 1px 8px rgba(0,0,0,.06)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                background: "#07a3b5",
                padding: "13px 22px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <TableChartIcon sx={{ fontSize: 20, color: "white" }} />
                <span
                  style={{
                    color: "white",
                    fontWeight: 700,
                    fontSize: "0.98rem",
                  }}
                >
                  Tabular View
                </span>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={handleProcessExport}
                  disabled={isLoading || !tableValid()}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "6px 16px",
                    border: "1.5px solid rgba(255,255,255,0.5)",
                    borderRadius: 8,
                    background: "rgba(255,255,255,0.15)",
                    color: "white",
                    cursor: tableValid() && !isLoading ? "pointer" : "not-allowed",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    fontFamily: "inherit",
                    opacity: tableValid() ? 1 : 0.5,
                    transition: "all .15s",
                  }}
                >
                  {isLoading ? "Loading..." : "Process"}
                </button>
                <button
                  onClick={handleExport}
                  disabled={isLoading || !tableValid()}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "6px 16px",
                    border: "1.5px solid rgba(255,255,255,0.5)",
                    borderRadius: 8,
                    background: "rgba(255,255,255,0.15)",
                    color: "white",
                    cursor: tableValid() && !isLoading ? "pointer" : "not-allowed",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    fontFamily: "inherit",
                    opacity: tableValid() ? 1 : 0.5,
                    transition: "all .15s",
                  }}
                >
                  {isLoading ? "Loading..." : "Download"}
                </button>
              </div>
            </div>

            <div style={{ padding: 22 }}>
              {!drilldown && (
                <>
                  {/* Template Bar */}
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 10,
                      alignItems: "center",
                      padding: "12px 16px",
                      background: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      borderRadius: 10,
                      marginBottom: 20,
                    }}
                  >
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 6 }}
                    >
                      <BookmarkIcon sx={{ fontSize: 17, color: "#f59e0b" }} />
                      <span
                        style={{
                          fontWeight: 700,
                          color: "#475569",
                          fontSize: "0.82rem",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Template Table:
                      </span>
                    </div>
                    <select
                      value={selTpl}
                      onChange={(e) => {
                        setSelTpl(e.target.value);
                        loadTpl(e.target.value);
                      }}
                      style={{
                        fontSize: "0.82rem",
                        border: "1.5px solid #e2e8f0",
                        borderRadius: 8,
                        padding: "6px 10px",
                        color: "#334155",
                        background: "white",
                        minWidth: 180,
                        outline: "none",
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                    >
                      <option value="">— Bawaan Pabrik —</option>
                      {tpls.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.template_name}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => openModal("table", "save", { tf })}
                      style={iconBtn("#07a3b5")}
                      title="Simpan"
                    >
                      <SaveIcon sx={{ fontSize: 16 }} />
                    </button>

                    {selTpl !== "" && (
                      <button
                        onClick={handleDeleteTpl}
                        style={iconBtn("#e74c3c")}
                        title="Hapus"
                      >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </button>
                    )}
                  </div>

                  <FilterZone
                    title="Filter Analitik Table"
                    icon={FilterAltIcon}
                    rightSlot={
                      <>
                        <button
                          onClick={reset}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                            border: "1.5px solid #e2e8f0",
                            background: "white",
                            borderRadius: 8,
                            padding: "7px 14px",
                            cursor: "pointer",
                            fontWeight: 600,
                            fontSize: "0.82rem",
                            color: "#475569",
                            fontFamily: "inherit",
                          }}
                        >
                          <RestartAltIcon sx={{ fontSize: 16 }} /> Reset
                        </button>
                        <button
                          onClick={generateTable}
                          disabled={!tableValid() || busy}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            border: "none",
                            borderRadius: 8,
                            padding: "7px 18px",
                            cursor:
                              tableValid() && !busy ? "pointer" : "not-allowed",
                            fontWeight: 700,
                            fontSize: "0.82rem",
                            fontFamily: "inherit",
                            background:
                              tableValid() && !busy ? "#07a3b5" : "#e2e8f0",
                            color: tableValid() && !busy ? "white" : "#94a3b8",
                            boxShadow:
                              tableValid() && !busy
                                ? "0 3px 10px rgba(7,163,181,0.3)"
                                : "none",
                            transition: "all .2s",
                          }}
                        >
                          <AutoFixHighIcon sx={{ fontSize: 16 }} />{" "}
                          {busy ? "Loading…" : "Terapkan"}
                        </button>
                      </>
                    }
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(4, 1fr)",
                        gap: 14,
                        marginBottom: 14,
                      }}
                    >
                      <SearchSelect
                        label="DB Set Name"
                        options={dims.DB}
                        value={tf.DB}
                        onChange={(v) =>
                          setTf((p) => ({ ...makeTableF(), DB: v }))
                        }
                        required
                      />
                      <MultiSearchSelect
                        label="Fact"
                        options={dims.Fact}
                        value={tf.Fact}
                        loading={loadingDims.Fact}
                        onChange={(v) => setTf((p) => ({ ...p, Fact: v }))}
                        required
                      />
                      <MultiSearchSelect
                        label="Period"
                        options={dims.Period}
                        value={tf.Period}
                        loading={loadingDims.Period}
                        onChange={(v) => setTf((p) => ({ ...p, Period: v }))}
                        required
                      />
                      <MultiSearchSelect
                        label="Market"
                        options={dims.Market}
                        value={tf.Market}
                        loading={loadingDims.Market}
                        onChange={(v) => setTf((p) => ({ ...p, Market: v }))}
                        required
                      />
                    </div>
                     <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: `repeat(${
                          1 +
                          (tf.Level?.includes("1_Category") || tf.Level?.includes("3_Brand") || tf.Level?.includes("4_Product") ? 1 : 0) +
                          (tf.Level?.includes("3_Brand") || tf.Level?.includes("4_Product") ? 1 : 0) +
                          (tf.Level?.includes("4_Product") ? 1 : 0)
                        }, 1fr)`,
                        gap: 14,
                      }}
                    >
                      <MultiSearchSelect
                        label="Level"
                        options={LEVEL_OPTION}
                        value={tf.Level}
                        onChange={(v) => {
                          setTf((p) => {
                            const newTf = { ...p, Level: v };
                            const hasBrand = v.includes("3_Brand") || v.includes("4_Product");
                            const hasProduct = v.includes("4_Product");
                            if (!hasBrand) {
                              newTf.Brand = [];
                              newTf.Product = [];
                            } else if (!hasProduct) {
                              newTf.Product = [];
                            }
                            return newTf;
                          });
                        }}
                      />
                      {tf.Level && (tf.Level.includes("1_Category") || tf.Level.includes("3_Brand") || tf.Level.includes("4_Product")) && (
                        <MultiSearchSelect
                          label="Category"
                          options={dims.Category}
                          value={tf.Category}
                          loading={loadingDims.Category}
                          onChange={(v) =>
                            setTf((p) => ({
                              ...p,
                              Category: v,
                              Brand: [],
                              Product: [],
                            }))
                          }
                        />
                      )}
                      {tf.Level && (tf.Level.includes("3_Brand") || tf.Level.includes("4_Product")) && (
                        <MultiSearchSelect
                          label="Brand"
                          options={brandOpts}
                          value={tf.Brand}
                          loading={loadingDims.Products}
                          onChange={(v) =>
                            setTf((p) => ({ ...p, Brand: v, Product: [] }))
                          }
                        />
                      )}
                      {tf.Level && tf.Level.includes("4_Product") && (
                        <MultiSearchSelect
                          label="Product"
                          options={prodOpts}
                          value={tf.Product}
                          loading={loadingDims.Products}
                          onChange={(v) => setTf((p) => ({ ...p, Product: v }))}
                        />
                      )}
                    </div>
                  </FilterZone>
                </>
              )}

              {drilldown && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 16,
                    background: "#f8fafc",
                    padding: "12px 16px",
                    borderRadius: 10,
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 12 }}
                  >
                    <button
                      onClick={() => {
                        setDrilldown(null);
                        setSearch("");
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        border: "1.5px solid #07a3b5",
                        background: "white",
                        color: "#07a3b5",
                        borderRadius: 8,
                        padding: "6px 14px",
                        cursor: "pointer",
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        fontFamily: "inherit",
                      }}
                    >
                      <ArrowBackIcon sx={{ fontSize: 16 }} /> Kembali ke Chart
                    </button>
                    <div style={{ fontSize: "0.9rem", color: "#334155" }}>
                      Data Drilldown: <b>{drilldown.period}</b> —{" "}
                      <b>{drilldown.item}</b>
                    </div>
                  </div>
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 14,
                }}
              >
                <div
                  style={{ position: "relative", width: "100%", maxWidth: 300 }}
                >
                  <SearchIcon
                    sx={{
                      position: "absolute",
                      left: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#94a3b8",
                      fontSize: 18,
                    }}
                  />
                  <input
                    type="text"
                    placeholder="Cari dalam tabel..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "8px 12px 8px 38px",
                      border: "1.5px solid #e2e8f0",
                      borderRadius: 8,
                      fontSize: "0.85rem",
                      outline: "none",
                      fontFamily: "inherit",
                      boxSizing: "border-box",
                    }}
                  />
                </div>
                <div style={{ fontSize: "0.85rem", color: "#64748b" }}>
                  Total: <b>{filteredData.length}</b> baris
                </div>
              </div>

              <div
                style={{
                  width: "100%",
                  overflowX: "auto",
                  border: "1px solid #e2e8f0",
                  borderRadius: 10,
                  background: "white",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    minWidth: 800,
                    fontSize: "0.82rem",
                  }}
                >
                  <thead>
                    <tr style={{ background: "#f8fafc" }}>
                      <th style={thStyle}>DB Set</th>
                      <th style={thStyle}>Market Breakdown</th>
                      <th style={thStyle}>Level</th>
                      <th style={thStyle}>Period</th>
                      <th style={thStyle}>Category</th>
                      <th style={thStyle}>Brand</th>
                      <th style={thStyle}>Product</th>
                      <th style={thStyle}>Fact</th>
                      <th style={thStyle}>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.length > 0 ? (
                      currentData.map((row, i) => (
                        <tr
                          key={i}
                          style={{ borderBottom: "1px solid #f1f5f9" }}
                        >
                          <td style={tdStyle}>
                            {row.DBSet || row.dbset || tf.DB}
                          </td>
                          <td style={tdStyle}>
                            {row.Market_Breakdown || row.market_breakdown}
                          </td>
                          <td style={tdStyle}>{row.Level || row.level}</td>
                          <td style={tdStyle}>{row.Period || row.period}</td>
                          <td style={tdStyle}>
                            {row.Category || row.category}
                          </td>
                          <td style={tdStyle}>{row.Brand || row.brand}</td>
                          <td style={tdStyle}>{row.Product || row.product}</td>
                          <td style={tdStyle}>
                            {row.Fact || row.fact || tf.Fact}
                          </td>
                          <td
                            style={{
                              ...tdStyle,
                              fontWeight: 700,
                              color: "#07a3b5",
                              textAlign: "right",
                            }}
                          >
                            {new Intl.NumberFormat("id-ID").format(
                              row.FactValue ??
                                row.factvalue ??
                                row.Value ??
                                row.value ??
                                0
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={9}
                          style={{
                            padding: "40px 20px",
                            textAlign: "center",
                            color: "#94a3b8",
                          }}
                        >
                          {busy
                            ? "Loading data..."
                            : "Tidak ada data untuk ditampilkan."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 8,
                    marginTop: 20,
                  }}
                >
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    style={pageBtnStyle(page === 1)}
                  >
                    Prev
                  </button>
                  <span
                    style={{
                      fontSize: "0.85rem",
                      color: "#475569",
                      display: "flex",
                      alignItems: "center",
                      padding: "0 10px",
                    }}
                  >
                    Halaman {page} dari {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    style={pageBtnStyle(page === totalPages)}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
